import React, { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { injectIntl, FormattedMessage } from 'react-intl';
import { toastr } from 'react-redux-toastr';
import moment from 'moment';

import Loader from '../../../components/Common/Loader/Loader';
import getAdverseActionState from './getAdverseActionState.graphql';
import issuePreAdverseActionMutation from './issuePreAdverseAction.graphql';
import issueFinalAdverseActionMutation from './issueFinalAdverseAction.graphql';
import { decode } from '../../../helpers/queryEncryption';
import messages from '../../../locale/messages';

// COMPLIANCE / FCRA (15 U.S.C. § 1681b(b)(3)) + CA fair-chance — admin screen that drives the
// adverse-action sequence: PRE_ADVERSE notice -> reasonable-wait window -> FINAL adverse action. The
// fail-closed gating (a prior pre-adverse whose waitUntil has elapsed + a recorded individualized
// assessment) is enforced server-side in the resolvers; this UI mirrors the state and surfaces the api's
// rejection messages as toasts. All three resolvers are requireAdmin (request.user.admin).
const PANEL = {
  background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: '18px 20px',
  marginTop: 16, maxWidth: 720,
};
const GREY = '#5B6472';

const AdverseAction = ({ intl: { formatMessage } }) => {
  const [userIdInput, setUserIdInput] = useState('');
  // Modal state: which prompt is open ('pre' | 'final' | null) + form fields.
  const [modal, setModal] = useState(null);
  const [reason, setReason] = useState('');
  const [reportCopyRef, setReportCopyRef] = useState('');
  const [assessment, setAssessment] = useState('');

  const [runQuery, { loading, data }] = useLazyQuery(getAdverseActionState, {
    fetchPolicy: 'network-only',
    ssr: false,
  });

  const [runPre, { loading: issuingPre }] = useMutation(issuePreAdverseActionMutation);
  const [runFinal, { loading: issuingFinal }] = useMutation(issueFinalAdverseActionMutation);

  const state = data?.getAdverseActionState;
  const loadedUserId = state?.status === 200 ? state?.userId : null;

  const lookup = () => {
    const id = String(userIdInput || '').trim();
    if (!id) {
      toastr.error('Error', formatMessage(messages.adverseUserIdRequired));
      return;
    }
    runQuery({ variables: { userId: id } });
  };

  const refresh = () => {
    if (loadedUserId) runQuery({ variables: { userId: loadedUserId } });
  };

  const closeModal = () => {
    setModal(null);
    setReason('');
    setReportCopyRef('');
    setAssessment('');
  };

  const submitPre = async () => {
    try {
      const res = await runPre({
        variables: {
          userId: loadedUserId,
          reason: String(reason || '').trim(),
          reportCopyRef: String(reportCopyRef || '').trim() || null,
        },
      });
      const r = res?.data?.issuePreAdverseAction;
      if (r?.status === 200) {
        toastr.success('Success', formatMessage(messages.adversePreSuccess));
        closeModal();
        refresh();
      } else {
        // Surface the api's rejection message (already issued / reason too short / etc.).
        toastr.error('Error', r?.errorMessage || 'Something went wrong');
      }
    } catch (e) {
      toastr.error('Error', 'Something went wrong');
    }
  };

  const submitFinal = async () => {
    try {
      const res = await runFinal({
        variables: {
          userId: loadedUserId,
          individualizedAssessment: String(assessment || '').trim(),
        },
      });
      const r = res?.data?.issueFinalAdverseAction;
      if (r?.status === 200) {
        toastr.success('Success', formatMessage(messages.adverseFinalSuccess));
        closeModal();
        refresh();
      } else {
        // Surface the fail-closed rejection (no pre-adverse / wait not elapsed / assessment missing).
        toastr.error('Error', r?.errorMessage || 'Something went wrong');
      }
    } catch (e) {
      toastr.error('Error', 'Something went wrong');
    }
  };

  const fmtDate = (v) => (v ? moment(Number(v)).format('DD-MM-YYYY HH:mm') : '-');

  const renderState = () => {
    if (!state) return null;
    if (state.status !== 200) {
      return <p style={{ color: '#B91C1C' }}>{state.errorMessage}</p>;
    }

    const name = `${decode(state.encryptFirstName) || ''} ${decode(state.encryptLastName) || ''}`.trim();

    let badge;
    if (state.hasFinal) {
      badge = { label: messages.adverseStateFinal, color: '#B91C1C' };
    } else if (state.hasPreAdverse) {
      badge = { label: messages.adverseStatePre, color: '#F59E0B' };
    } else {
      badge = { label: messages.adverseStateNone, color: '#0E9488' };
    }

    return (
      <div style={PANEL}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>
              {name || '-'} <span style={{ color: GREY, fontWeight: 400 }}>· #{state.profileId}</span>
            </div>
            <div style={{ color: GREY, fontSize: 13 }}>{decode(state.encryptEmail)}</div>
            <div style={{ color: GREY, fontSize: 12 }}>userId: {state.userId}</div>
          </div>
          <span style={{ color: badge.color, fontWeight: 700, alignSelf: 'flex-start' }}>
            <FormattedMessage {...badge.label} />
          </span>
        </div>

        {state.hasPreAdverse && !state.hasFinal && (
          <div style={{ marginTop: 12, fontSize: 13, color: GREY }}>
            <FormattedMessage {...messages.adverseWaitUntil} />: <strong>{fmtDate(state.waitUntil)}</strong>
            {' — '}
            {state.waitElapsed
              ? <span style={{ color: '#0E9488', fontWeight: 600 }}><FormattedMessage {...messages.adverseWaitElapsed} /></span>
              : <span style={{ color: '#F59E0B', fontWeight: 600 }}><FormattedMessage {...messages.adverseWaitPending} /></span>}
          </div>
        )}

        <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={state.hasPreAdverse || state.hasFinal || issuingPre}
            onClick={() => setModal('pre')}
          >
            <FormattedMessage {...messages.adverseIssuePre} />
          </button>
          <button
            type="button"
            className="btn btn-danger"
            // Enabled only when a pre-adverse exists, its wait has elapsed, and no final yet. The
            // resolver re-checks this regardless.
            disabled={!state.hasPreAdverse || !state.waitElapsed || state.hasFinal || issuingFinal}
            onClick={() => setModal('final')}
          >
            <FormattedMessage {...messages.adverseIssueFinal} />
          </button>
        </div>

        {Array.isArray(state.records) && state.records.length > 0 && (
          <table className="table" style={{ width: '100%', marginTop: 18 }}>
            <thead>
              <tr>
                <th scope="col"><FormattedMessage {...messages.adverseStage} /></th>
                <th scope="col"><FormattedMessage {...messages.adverseReason} /></th>
                <th scope="col"><FormattedMessage {...messages.adverseIssuedAt} /></th>
                <th scope="col"><FormattedMessage {...messages.adverseWaitUntil} /></th>
              </tr>
            </thead>
            <tbody>
              {state.records.map((rec) => (
                <tr key={rec.id}>
                  <td data-label={formatMessage(messages.adverseStage)}>
                    <strong>{rec.stage}</strong>
                  </td>
                  <td data-label={formatMessage(messages.adverseReason)}>
                    {rec.reason || '-'}
                    {rec.reportCopyRef ? ` (ref: ${rec.reportCopyRef})` : ''}
                    {rec.individualizedAssessment ? ` — ${rec.individualizedAssessment}` : ''}
                  </td>
                  <td data-label={formatMessage(messages.adverseIssuedAt)}>{fmtDate(rec.issuedAt)}</td>
                  <td data-label={formatMessage(messages.adverseWaitUntil)}>{fmtDate(rec.waitUntil)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  return (
    <Loader type={'page'} show={loading}>
      <h1 className="siteAdminPrimaryHeading"><FormattedMessage {...messages.adverseActionTitle} /></h1>
      <p style={{ color: GREY, maxWidth: 720 }}>
        <FormattedMessage {...messages.adverseActionIntro} />
      </p>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
        <input
          type="text"
          className="form-control"
          style={{ maxWidth: 240 }}
          placeholder={formatMessage(messages.adverseUserIdPlaceholder)}
          value={userIdInput}
          onChange={(e) => setUserIdInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') lookup(); }}
        />
        <button type="button" className="btn btn-primary" onClick={lookup} disabled={loading}>
          <FormattedMessage {...messages.adverseLookup} />
        </button>
      </div>

      {renderState()}

      {/* Issue Pre-Adverse prompt */}
      {modal === 'pre' && (
        <div style={overlay}>
          <div style={modalBox}>
            <h3 style={{ marginTop: 0 }}><FormattedMessage {...messages.adverseIssuePre} /></h3>
            <label style={labelStyle}><FormattedMessage {...messages.adverseReason} /> *</label>
            <textarea
              className="form-control"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={formatMessage(messages.adverseReasonPlaceholder)}
            />
            <label style={labelStyle}><FormattedMessage {...messages.adverseReportCopyRef} /></label>
            <input
              type="text"
              className="form-control"
              value={reportCopyRef}
              onChange={(e) => setReportCopyRef(e.target.value)}
              placeholder={formatMessage(messages.adverseReportCopyRefPlaceholder)}
            />
            <div style={modalActions}>
              <button type="button" className="btn btn-default" onClick={closeModal} disabled={issuingPre}>
                <FormattedMessage {...messages.adverseCancel} />
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={submitPre}
                disabled={issuingPre || String(reason || '').trim().length < 3}
              >
                <FormattedMessage {...messages.adverseConfirm} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Issue Final prompt */}
      {modal === 'final' && (
        <div style={overlay}>
          <div style={modalBox}>
            <h3 style={{ marginTop: 0 }}><FormattedMessage {...messages.adverseIssueFinal} /></h3>
            <p style={{ color: GREY, fontSize: 13 }}>
              <FormattedMessage {...messages.adverseAssessmentHelp} />
            </p>
            <label style={labelStyle}><FormattedMessage {...messages.adverseAssessment} /> *</label>
            <textarea
              className="form-control"
              rows={5}
              value={assessment}
              onChange={(e) => setAssessment(e.target.value)}
              placeholder={formatMessage(messages.adverseAssessmentPlaceholder)}
            />
            <div style={modalActions}>
              <button type="button" className="btn btn-default" onClick={closeModal} disabled={issuingFinal}>
                <FormattedMessage {...messages.adverseCancel} />
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={submitFinal}
                disabled={issuingFinal || String(assessment || '').trim().length < 10}
              >
                <FormattedMessage {...messages.adverseConfirm} />
              </button>
            </div>
          </div>
        </div>
      )}
    </Loader>
  );
};

const overlay = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center',
  justifyContent: 'center', zIndex: 1050, padding: 16,
};
const modalBox = {
  background: '#fff', borderRadius: 14, padding: '22px 24px',
  width: '100%', maxWidth: 520, boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
};
const labelStyle = { display: 'block', fontWeight: 600, fontSize: 13, margin: '12px 0 4px' };
const modalActions = { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 };

export default injectIntl(AdverseAction);
